import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { ktmToday } from '../../utils/calendar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdAdd, MdClose } from 'react-icons/md';
import DualDatePicker from '../common/DualDatePicker';
import EditDetailsModal, { Field, inputCls } from '../common/EditDetailsModal';
import useClubVocab from '../../hooks/useClubVocab';
import { useTenant } from '../../context/TenantContext';
import { hasBelts } from '../../utils/publicProfile';
import { updateStudent } from '../../services/studentService';
import { useState } from 'react';
import { sanitizePhone, isValidPhone, PHONE_HINT } from '../../utils/inputRules';
import { toWhatsAppPhone } from '../../utils/whatsapp';
import { calcAge } from '../../utils/age';

const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

// Admin "Edit Details" for a member. Pre-filled with current values; PATCHes only the personal
// fields (login email is Phase 2). Guardian wording flips to "Emergency Contact" for non-belt clubs
// via the same vocab/relabel pattern as the Add Member modal. Taekwondo renders byte-identical.
export default function EditStudentModal({ student, onClose, onSaved }) {
  const v = useClubVocab();
  const { club } = useTenant();
  const showBelts = hasBelts(club?.clubType);
  const qc = useQueryClient();
  const hasLogin = !!(student.user?.email && !student.user.email.includes('@noemail.club') && !student.user?.isRecordOnly);

  const [guardianPhoneError, setGuardianPhoneError] = useState(false);

  const { register, control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      fullName:    student.fullName || student.user?.name || '',
      phone:       student.phone || '',
      dateOfBirth: isoDay(student.dateOfBirth),
      gender:      student.gender || '',
      address: {
        street:   student.address?.street   || '',
        city:     student.address?.city     || '',
        district: student.address?.district || '',
        province: student.address?.province || '',
      },
      emergencyContact: {
        name:         student.emergencyContact?.name         || '',
        phone:        student.emergencyContact?.phone        || '',
        relationship: student.emergencyContact?.relationship || '',
      },
      guardians: (student.guardians || []).map(g => ({
        name: g.name || '', phone: g.phone || '', relationship: g.relationship || '',
      })),
      medicalInfo: student.medicalInfo || '',
      attendanceNotificationsOptOut: !!student.attendanceNotificationsOptOut,
    },
  });
  const guardians = useFieldArray({ control, name: 'guardians' });

  // Under 18 → at least one guardian needs a valid phone (they're the real contact). Checked
  // against the LIVE dateOfBirth value, not the one the student was created with, so editing a
  // DOB that crosses the 18 boundary re-evaluates immediately.
  const dobValue = watch('dateOfBirth');
  const currentAge = dobValue ? calcAge(dobValue) : null;
  const guardianPhoneRequired = currentAge != null && currentAge < 18;

  const mutation = useMutation({
    mutationFn: (payload) => updateStudent(student._id, payload),
    onSuccess: () => {
      toast.success('Details updated');
      qc.invalidateQueries({ queryKey: ['student-full', student._id] });
      qc.invalidateQueries({ queryKey: ['students'] });
      onSaved?.();
      onClose?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update details'),
  });

  const onSubmit = (vals) => {
    const guardianList = vals.guardians || [];
    const hasValidGuardianPhone = guardianList.some(g => toWhatsAppPhone(g.phone) !== null);
    if (guardianPhoneRequired && !hasValidGuardianPhone) {
      setGuardianPhoneError(true);
      return;
    }
    setGuardianPhoneError(false);
    mutation.mutate({
      fullName:    vals.fullName?.trim(),
      phone:       vals.phone?.trim() || '',
      dateOfBirth: vals.dateOfBirth || '',
      gender:      vals.gender || '',
      address:     vals.address,
      emergencyContact: vals.emergencyContact,
      guardians: (vals.guardians || [])
        .filter(g => g.name?.trim() || g.phone?.trim())
        .map(g => ({ name: g.name?.trim(), phone: g.phone?.trim(), relationship: g.relationship?.trim() })),
      medicalInfo: vals.medicalInfo?.trim() || '',
      attendanceNotificationsOptOut: !!vals.attendanceNotificationsOptOut,
    });
  };

  const guardianLabel = showBelts ? 'Parent / Guardian' : 'Emergency Contact';

  return (
    <EditDetailsModal
      title={`Edit ${v.member}`}
      subtitle="Update personal details. Changes apply immediately."
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      saving={mutation.isPending}
      hasLogin={hasLogin}
    >
      {/* Identity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Full Name" required error={errors.fullName?.message}>
          <input className={inputCls(errors.fullName)}
            {...register('fullName', { required: 'Name is required', maxLength: { value: 50, message: 'At most 50 characters' } })} />
        </Field>
        <Field label="Phone" error={errors.phone?.message}>
          <input type="tel" placeholder="97/98XXXXXXXX" className={inputCls(errors.phone)}
            {...register('phone', { validate: (val) => !val || isValidPhone(val) || PHONE_HINT })}
            onChange={(e) => setValue('phone', sanitizePhone(e.target.value), { shouldValidate: true })} />
        </Field>
        <Controller name="dateOfBirth" control={control}
          render={({ field }) => (
            <DualDatePicker label="Date of Birth" value={field.value || ''} onChange={field.onChange}
              max={ktmToday()} fastNav />
          )} />
        <Field label="Gender">
          <select className={inputCls()} {...register('gender')}>
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </Field>
      </div>

      {/* Address */}
      <div className="pt-1">
        <p className="text-xs font-semibold text-muted mb-2">Address</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Street"><input className={inputCls()} {...register('address.street')} /></Field>
          <Field label="City"><input className={inputCls()} {...register('address.city')} /></Field>
          <Field label="District"><input className={inputCls()} {...register('address.district')} /></Field>
          <Field label="Province"><input className={inputCls()} {...register('address.province')} /></Field>
        </div>
      </div>

      {/* Emergency contact (the structured emergencyContact object shown in the drawer) */}
      <div className="pt-1">
        <p className="text-xs font-semibold text-muted mb-2">Emergency Contact</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Name"><input className={inputCls()} {...register('emergencyContact.name')} /></Field>
          <Field label="Phone">
            <input type="tel" className={inputCls()} {...register('emergencyContact.phone')}
              onChange={(e) => setValue('emergencyContact.phone', sanitizePhone(e.target.value))} />
          </Field>
          <Field label="Relationship"><input className={inputCls()} {...register('emergencyContact.relationship')} /></Field>
        </div>
      </div>

      {/* Guardians — label flips by club type (same fields) */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted">
            {guardianLabel}{guardianPhoneRequired && <span className="text-red-400"> * (phone required, under 18)</span>}
          </p>
          {guardians.fields.length < 5 && (
            <button type="button"
              onClick={() => guardians.append({ name: '', phone: '', relationship: '' })}
              className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline">
              <MdAdd className="text-sm" /> Add
            </button>
          )}
        </div>
        {guardians.fields.length === 0 && <p className="text-xs text-subtle">None added.</p>}
        {guardianPhoneError && (
          <p className="text-xs text-red-400 mb-2">A parent/guardian phone number is required for students under 18.</p>
        )}
        <div className="space-y-3">
          {guardians.fields.map((f, i) => (
            <div key={f.id} className="relative bg-nav-hover/40 border border-divider rounded-xl p-3">
              <button type="button" onClick={() => guardians.remove(i)}
                className="absolute top-2 right-2 p-1 text-subtle hover:text-red-400 rounded-lg" aria-label="Remove">
                <MdClose className="text-sm" />
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Name"><input className={inputCls()} {...register(`guardians.${i}.name`)} /></Field>
                <Field label="Phone">
                  <input type="tel" className={inputCls()} {...register(`guardians.${i}.phone`)}
                    onChange={(e) => setValue(`guardians.${i}.phone`, sanitizePhone(e.target.value))} />
                </Field>
                <Field label="Relationship"><input className={inputCls()} {...register(`guardians.${i}.relationship`)} /></Field>
              </div>
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 mt-3 cursor-pointer">
          <input type="checkbox" className="accent-primary" {...register('attendanceNotificationsOptOut')} />
          <span className="text-xs text-muted">Don't send this family attendance messages (even when the club has them turned on)</span>
        </label>
      </div>

      {/* Medical */}
      <Field label="Medical Conditions / Injuries">
        <textarea rows={2} placeholder="Anything trainers should know for safety"
          className={`${inputCls()} resize-none`} {...register('medicalInfo')} />
      </Field>
    </EditDetailsModal>
  );
}
